import type { components } from '@connected/api-client/api.generated';

import type { ClassificationData, DistributionBoardObservationKind, ObservationObjectKinds, ObservationParentKinds, ObservationReport } from './observation-types';

import { DISTRIBUTION_BOARDS_OBSERVATION_KINDS, FINAL_CIRCUITS_OBSERVATION_KINDS } from './observation-constants';

export function filterReportsByObjectAndParentKind(
    reports: ObservationReport[],
    objectKind: ObservationObjectKinds,
    parentKind: ObservationParentKinds,
): ObservationReport[] {
    // If objectKind is 'DistributionBoardDevice', exclude Scope12
    if (objectKind === 'DistributionBoardDevice') {
        return reports?.filter(report => report.inspectionKind !== 'Scope12');
    }

    // If parentKind is 'PVInverter', only show Scope12
    if (parentKind === 'PVInverter') {
        return reports?.filter(report => report.inspectionKind === 'Scope12');
    }

    // Otherwise, return all reports
    return reports;
}

export type GetClassificationGroupDataProps = {
    classificationData: ClassificationData | undefined;
    electricalObservationClassificationGroupId: string | null | undefined;
};

export type GetClassificationCodeDataProps = {
    classificationData: ClassificationData | undefined;
    body: {
        electricalObservationClassificationGroupId: string | null | undefined;
        electricalObservationClassificationCodeId: string | null | undefined;
    };
};

export function getClassificationGroupData({
    classificationData,
    electricalObservationClassificationGroupId,
}: GetClassificationGroupDataProps) {
    const foundGroup = classificationData?.Groups?.find(
        group => group.ElectricalObservationClassificationGroupId === electricalObservationClassificationGroupId,
    );

    return foundGroup;
}

export function getClassificationCodeData({
    classificationData,
    body,
}: GetClassificationCodeDataProps) {
    const foundGroup = getClassificationGroupData({
        classificationData,
        electricalObservationClassificationGroupId: body?.electricalObservationClassificationGroupId,
    });

    const foundClassification = foundGroup?.Codes?.find(
        x => x.ElectricalObservationClassificationCodeId === body?.electricalObservationClassificationCodeId,
    );

    return foundClassification;
}

/**
 * Type predicate to check if an ObjectKind is a distribution board observation kind.
 * This provides type narrowing for TypeScript.
 */
export function isDistributionBoardObservationKind(
    objectKind: components['schemas']['ElectricalProjectObservationObjectKind'],
): objectKind is DistributionBoardObservationKind {
    return (DISTRIBUTION_BOARDS_OBSERVATION_KINDS as readonly string[]).includes(objectKind);
}

export function isFinalCircuitObservationKind(
    objectKind: components['schemas']['ElectricalProjectObservationObjectKind'],
): objectKind is 'FinalCircuit' {
    return (FINAL_CIRCUITS_OBSERVATION_KINDS as readonly string[]).includes(objectKind);
}
